import styled from 'styled-components';

export const CardContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;

export const ActivitySectionRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

export const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AvatarWrapper = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  font-family: Inter, sans-serif;
  color: ${(props) => props.theme.colors.activeBlue};
  background: ${(props) => props.$color ?? props.theme.colors.titanWhite};
`;

export const IdentityBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const RoleChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const CompanyText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const CardContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 6px;
`;

export const NextBestActionBlock = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  line-height: 1.45;
  width: 100%;
  border: 1px solid #F3E9FD;
  border-radius: 8px;
  box-sizing: border-box;
`;

const NEXT_BEST_ACTION_TEXT_COLOR = '#2C075C';

export const NextBestActionLabel = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const NextBestActionText = styled.span`
  flex: 1;
  min-width: 0;
  color: ${NEXT_BEST_ACTION_TEXT_COLOR};

  & * {
    color: inherit !important;
  }
`;

export const NextBestActionLabelText = styled.span`
  color: ${NEXT_BEST_ACTION_TEXT_COLOR};

  & * {
    color: inherit !important;
  }
`;

export const NextBestActionContentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

export const NextBestActionTruncated = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;

  & > span {
    display: block !important;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ViewMoreLink = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.text?.label || '#2a2a2a'};
  text-decoration: underline;
  text-decoration-skip-ink: none;
  /* Invisible but occupies space until ellipsis applies; then visible */
  visibility: ${(props) => (props.$isVisible ? 'visible' : 'hidden')};
  pointer-events: ${(props) => (props.$isVisible ? 'auto' : 'none')};

  &:hover {
    opacity: 0.8;
  }
`;

const ACTIVITY_SECTION_STYLES = `
  border-radius: var(--Radius-Radius-M, 8px);
  border: 1px solid var(--Border-Neutral-Subtle, #F2F2F2);
  background: rgba(250, 250, 250, 0.6);
  box-sizing: border-box;
`;

export const ActivitySectionWrapper = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  ${ACTIVITY_SECTION_STYLES}
`;

export const ActionsSectionWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const ActivityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const ActivityItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const ActivitySeparator = styled.span`
  color: ${(props) => props.theme.colors.border};
  font-size: 10px;
  user-select: none;
`;

export const IdentitySeparator = styled.span`
  color: #99a1af;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  user-select: none;
  flex-shrink: 0;
`;

export const RightBlock = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
`;

export const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BORDER_RADIUS = 'var(--Radius-Radius-M, 8px)';
const ELEVATION_5 = '8px 8px 16px 0 rgba(0, 0, 0, 0.08)';

const SEVERITY_STYLES = {
  alert: {
    borderRadius: BORDER_RADIUS,
    border: '0.5px solid var(--Border-Alert-Subtle, #EDC6A6)',
    background:
      'var(--Orange-BG, radial-gradient(175.36% 136.87% at 18.46% 91.07%, #FFF4E5 0%, #FFF 80.17%))',
  },
  error: {
    borderRadius: BORDER_RADIUS,
    border: '0.5px solid var(--Border-Error-Subtle, #EA9191)',
    background:
      'var(--Red-BG, radial-gradient(175.36% 136.87% at 18.46% 91.07%, #FDEDED 0%, #FFF 80.17%))',
  },
};

const HOVER_STYLES = {
  neutral: {
    borderRadius: BORDER_RADIUS,
    border: '0.5px solid var(--Border-Neutral-Default, #5C5C5C)',
    background: 'var(--Surface-Neutral-White, #FFF)',
    boxShadow: ELEVATION_5,
  },
  alert: {
    borderRadius: BORDER_RADIUS,
    border: '0.5px solid var(--Border-Alert-Lighter, #B25102)',
    background:
      'var(--Orange-BG, radial-gradient(175.36% 136.87% at 18.46% 91.07%, #FFF4E5 0%, #FFF 80.17%))',
    boxShadow: ELEVATION_5,
  },
  error: {
    borderRadius: BORDER_RADIUS,
    border: '0.5px solid var(--Border-Error-Lighter, #D32F2F)',
    background:
      'var(--Red-BG, radial-gradient(175.36% 136.87% at 18.46% 91.07%, #FDEDED 0%, #FFF 80.17%))',
    boxShadow: ELEVATION_5,
  },
};

export const CardHighlightWrapper = styled.div`
  cursor: pointer;

  ${({ $severity }) => {
    const style = SEVERITY_STYLES[$severity];
    const hoverStyle = HOVER_STYLES[$severity ?? 'neutral'];
    const baseStyles = style
      ? `
      > div {
        border-radius: ${style.borderRadius};
        border: ${style.border};
        background: ${style.background};
      }
    `
      : '';
    const hoverStyles = hoverStyle
      ? `
      &:hover > div {
        border-radius: ${hoverStyle.borderRadius};
        border: ${hoverStyle.border};
        background: ${hoverStyle.background};
        box-shadow: ${hoverStyle.boxShadow};
      }
    `
      : '';
    return baseStyles + hoverStyles;
  }}
`;
